import {
	Component,
	Input,
	Output,
	EventEmitter,
	OnInit,
	OnDestroy,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	forwardRef,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { Subject, takeUntil } from "rxjs";
import { TipoRiesgoSocialViewModel } from "src/app/_core/models/centros/cebe-pal.model";

// Models

// Services
import { MasterService } from "src/app/_core/providers/master.service";
import { NotificationService } from "src/app/_core/providers/notification.service";

@Component({
    selector: "app-tipo-riesgo-social-autocomplete",
    templateUrl: "./tipo-riesgo-social-autocomplete.component.html",
    styleUrls: ["./tipo-riesgo-social-autocomplete.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => TipoRiesgoSocialAutocompleteComponent),
            multi: true,
        },
    ],
    standalone: false
})
export class TipoRiesgoSocialAutocompleteComponent
	implements OnInit, OnDestroy, ControlValueAccessor
{
	private readonly destroy$ = new Subject<void>();

	// Inputs
	@Input() label = "Riesgo Social";
	@Input() placeholder = "Buscar riesgo social por código o descripción";
	@Input() required = false;
	@Input() disabled = false;
	@Input() hint = "Busque por código o descripción del riesgo social";

	// Outputs
	@Output() tipoRiesgoSocialSelected =
		new EventEmitter<TipoRiesgoSocialViewModel>();

	// State
	tiposRiesgoSocial: TipoRiesgoSocialViewModel[] = [];
	tipoRiesgoSocialId: number | null = null;
	selectedTipoRiesgoSocial: TipoRiesgoSocialViewModel | null = null;

	// ControlValueAccessor
	private onChange: (value: number | null) => void = () => {};
	private onTouched: () => void = () => {};

	constructor(
		private readonly masterService: MasterService,
		private readonly notificationService: NotificationService,
		private readonly cdr: ChangeDetectorRef,
	) {}

	ngOnInit(): void {
		this.loadTiposRiesgoSocial();
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	// ControlValueAccessor implementation
	writeValue(value: number | null): void {
		this.tipoRiesgoSocialId = value ?? null;
		this.syncSelected();
		this.cdr.markForCheck();
	}

	registerOnChange(fn: (value: number | null) => void): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	setDisabledState(isDisabled: boolean): void {
		this.disabled = isDisabled;
		this.cdr.markForCheck();
	}

	/**
	 * Carga la lista de tipos de riesgo social desde la API
	 */
	private loadTiposRiesgoSocial(): void {
		this.masterService
			.getTipoRiesgoSocialList()
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (tipos) => {
					this.tiposRiesgoSocial = tipos;
					this.syncSelected();
					this.cdr.markForCheck();
				},
				error: (error) => {
					console.error(
						"Error loading tipos de riesgo social:",
						error,
					);
					this.notificationService.showError(
						"Error al cargar los tipos de riesgo social",
					);
				},
			});
	}

	/** Recalcula el objeto seleccionado a partir del id actual. */
	private syncSelected(): void {
		this.selectedTipoRiesgoSocial =
			this.tipoRiesgoSocialId != null
				? (this.tiposRiesgoSocial.find(
						(t) => t.id === this.tipoRiesgoSocialId,
					) ?? null)
				: null;
	}

	/**
	 * Función de display para el autocomplete (arrow function para binding)
	 */
	displayTipoRiesgoSocial = (
		tipo: TipoRiesgoSocialViewModel | string,
	): string => {
		if (typeof tipo === "string") {
			return tipo;
		}
		// Validar que todos los campos existan para evitar "undefined - undefined"
		return tipo && tipo.codigo && tipo.descripcion
			? `${tipo.codigo} - ${tipo.descripcion}`
			: "";
	};

	/** Selección desde el autocomplete `.ds`. */
	onValueChange(tipo: TipoRiesgoSocialViewModel | null): void {
		this.tipoRiesgoSocialId = tipo ? tipo.id : null;
		this.selectedTipoRiesgoSocial = tipo;
		this.onChange(this.tipoRiesgoSocialId);
		this.onTouched();
		if (tipo) {
			this.tipoRiesgoSocialSelected.emit(tipo);
		}
		this.cdr.markForCheck();
	}

	onControlTouched(): void {
		this.onTouched();
	}
}
